import { createStore } from './util'
import { useApp } from './app'
import { Point, EpochPoints, PointHistoryItem, PointsErrorResponse } from '@/types'
import { ReferralHistoryItem } from './referral'
import { errorLog } from '@/utils'

// June 4th, 2025 cutoff date for epoch 0/1 split
const EPOCH_CUTOFF_DATE = '2025-06-04T12:00:00Z'
const EPOCH_0_START_DATE = '2024-01-01T00:00:00.000Z'

interface EpochStore {
  isLoading: boolean
  pointsHistory: Point[]
  referralHistory: ReferralHistoryItem[]
  epochsData: EpochPoints[] | null
  fetchEpochData: () => Promise<void>
  getPointsForRange: (startDate: Date, endDate?: Date) => Point[]
  getReferralsForRange: (startDate: Date, endDate?: Date) => ReferralHistoryItem[]
  getCurrentEpoch: () => EpochPoints | null
}

export const useEpoch = createStore<EpochStore>(
  'epoch',
  (set, get) => ({
    isLoading: false,
    pointsHistory: [],
    referralHistory: [],
    epochsData: null,

    fetchEpochData: async () => {
      // Skip if already loaded or currently loading
      if (get().epochsData !== null || get().isLoading) return

      set({ isLoading: true })

      try {
        const http = useApp.getState().http

        // Fetch ALL historical data in parallel
        const [pointsResponse, referralsResponse] = await Promise.all([
          http.get<PointHistoryItem[] | PointsErrorResponse>(
            `/point/history?start_date=${EPOCH_0_START_DATE}`,
          ),
          http.get<{ history: ReferralHistoryItem[] }>(
            `/referral/history?start_date=${EPOCH_0_START_DATE}`,
          ),
        ])

        // Handle points response
        let pointsHistory: Point[] = []
        if (pointsResponse.data && !('error' in pointsResponse.data)) {
          pointsHistory = pointsResponse.data.map((item: PointHistoryItem) => ({
            value: item.points,
            created_at: item.created_at,
          }))
        }

        // Handle referrals response
        const referralHistory = referralsResponse.data?.history || []

        // Generate epochs from data
        const epochsData = generateEpochsFromData(pointsHistory, referralHistory)

        set({
          pointsHistory,
          referralHistory,
          epochsData,
        })
      } catch (error) {
        errorLog(error)
        set({ epochsData: [] })
      } finally {
        set({ isLoading: false })
      }
    },

    getPointsForRange: (startDate: Date, endDate?: Date): Point[] => {
      const { pointsHistory } = get()
      const end = endDate || new Date()

      return pointsHistory.filter((point) => {
        const pointDate = new Date(point.created_at)
        return pointDate >= startDate && pointDate <= end
      })
    },

    getReferralsForRange: (startDate: Date, endDate?: Date): ReferralHistoryItem[] => {
      const { referralHistory } = get()
      const end = endDate || new Date()

      return referralHistory.filter((referral) => {
        const referralDate = new Date(referral.createdAt)
        return referralDate >= startDate && referralDate <= end
      })
    },

    getCurrentEpoch: (): EpochPoints | null => {
      const { epochsData } = get()
      if (!epochsData || epochsData.length === 0) return null
      return epochsData[epochsData.length - 1]
    },
  }),
  false,
)

// Helper functions for epoch calculations

const getEpochNumber = (currentDatetime: Date, cutoffDatetime: Date): number => {
  if (currentDatetime < cutoffDatetime) {
    return 0
  }
  const diffInWeeks = Math.floor(
    (currentDatetime.getTime() - cutoffDatetime.getTime()) / (7 * 24 * 60 * 60 * 1000),
  )
  return diffInWeeks + 1
}

const getCurrentEpochs = (): { currentEpoch: number; cutoffDatetime: Date } => {
  const cutoffDatetime = new Date(EPOCH_CUTOFF_DATE)
  const currentDatetime = new Date()
  const currentEpoch = getEpochNumber(currentDatetime, cutoffDatetime)

  return { currentEpoch, cutoffDatetime }
}

const generateEpochsFromData = (
  points: Point[],
  referrals: ReferralHistoryItem[],
): EpochPoints[] => {
  const { currentEpoch, cutoffDatetime } = getCurrentEpochs()
  const epochs: EpochPoints[] = []

  for (let epochNum = 0; epochNum <= currentEpoch; epochNum++) {
    const epoch = calculateEpochPoints(points, referrals, epochNum, cutoffDatetime)
    epochs.push(epoch)
  }

  return epochs
}

const calculateEpochPoints = (
  points: Point[],
  referrals: ReferralHistoryItem[],
  epochNumber: number,
  cutoffDatetime: Date,
): EpochPoints => {
  const currentDatetime = new Date()

  const { epochStart, epochEnd } = getEpochDateRange(epochNumber, cutoffDatetime, currentDatetime)

  // Filter points and referrals for this epoch
  const epochPoints = points.filter((point) => {
    const pointDate = new Date(point.created_at)
    return pointDate >= epochStart && pointDate < epochEnd
  })

  const epochReferrals = referrals.filter((referral) => {
    const referralDate = new Date(referral.createdAt)
    return referralDate >= epochStart && referralDate < epochEnd
  })

  const dailyPoints = generateDailyPoints(
    epochPoints,
    epochReferrals,
    epochStart,
    epochEnd,
    epochNumber,
    currentDatetime,
    cutoffDatetime,
  )

  const totalEarningPoints = dailyPoints.reduce((sum, day) => sum + day.earning_points, 0)
  const totalReferralPoints = dailyPoints.reduce((sum, day) => sum + day.referral_points, 0)

  return {
    epoch_number: epochNumber,
    start_date: epochStart.toISOString(),
    end_date: epochEnd.toISOString(),
    total_earning_points: totalEarningPoints,
    total_referral_points: totalReferralPoints,
    daily_points: dailyPoints,
  }
}

const getEpochDateRange = (
  epochNumber: number,
  cutoffDatetime: Date,
  currentDatetime: Date,
): { epochStart: Date; epochEnd: Date } => {
  let epochStart: Date
  let epochEnd: Date

  if (epochNumber === 0) {
    epochStart = new Date(EPOCH_0_START_DATE)
    epochEnd = new Date(cutoffDatetime.getTime() - 1000)
  } else {
    const weeksFromCutoff = epochNumber - 1
    epochStart = new Date(cutoffDatetime.getTime() + weeksFromCutoff * 7 * 24 * 60 * 60 * 1000)
    epochEnd = new Date(epochStart.getTime() + 7 * 24 * 60 * 60 * 1000 - 1000)
  }

  const currentEpochNumber = getEpochNumber(currentDatetime, cutoffDatetime)
  if (epochNumber < currentEpochNumber && epochEnd > currentDatetime) {
    epochEnd = currentDatetime
  }

  return { epochStart, epochEnd }
}

const generateDailyPoints = (
  points: Point[],
  referrals: ReferralHistoryItem[],
  epochStart: Date,
  epochEnd: Date,
  epochNumber: number,
  currentDatetime: Date,
  cutoffDatetime: Date,
) => {
  const dailyPoints: { date: string; earning_points: number; referral_points: number }[] = []

  let iterationDate = new Date(epochStart)

  while (iterationDate < epochEnd) {
    const nextDate = new Date(iterationDate.getTime() + 24 * 60 * 60 * 1000)

    const currentEpochNumber = getEpochNumber(currentDatetime, cutoffDatetime)
    if (iterationDate > currentDatetime && epochNumber < currentEpochNumber) {
      iterationDate = nextDate
      continue
    }

    const dayPoints = points
      .filter((point) => {
        const pointDate = new Date(point.created_at)
        return pointDate >= iterationDate && pointDate < nextDate
      })
      .reduce((sum, point) => sum + (point.value || 0), 0)

    const dayReferrals = referrals
      .filter((referral) => {
        const referralDate = new Date(referral.createdAt)
        return referralDate >= iterationDate && referralDate < nextDate
      })
      .reduce((sum, referral) => sum + (referral.pointsRewarded || 0), 0)

    const shouldInclude = epochNumber > 0 || dayPoints > 0 || dayReferrals > 0

    if (shouldInclude) {
      dailyPoints.push({
        date: iterationDate.toISOString(),
        earning_points: dayPoints,
        referral_points: dayReferrals,
      })
    }

    iterationDate = nextDate
  }

  return dailyPoints
}
